import { Button, Card, Divider, Input, Modal, Select, Space, Switch, Typography } from 'antd';
import Title from 'antd/es/typography/Title';
import { v4 as uuidv4 } from 'uuid';
import TipTap from '../../Sections/TipTap';
import { activeTabMap } from '../../constants';
import { defaultData } from '../..';
import { useState } from 'react';

interface ScopeProps {
  data: any;
  setData: (path: string, value: any) => void;
  readonly?: boolean;
}

const Scope: React.FC<ScopeProps> = ({ data, setData, readonly = false }) => {
  const impactLevels = [
    { value: 'none', label: 'None' },
    { value: 'low', label: 'Low' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
    { value: 'variable', label: 'Variable' }
  ];

  const risks = [
    { value: 'none', label: 'None' },
    { value: 'low', label: 'Low' },
    { value: 'low_to_medium', label: 'Low to Medium' },
    { value: 'medium', label: 'Medium' },
    { value: 'medium_to_high', label: 'Medium to High' },
    { value: 'high', label: 'High' }
  ];

  const approachTypes = [
    { value: 'none', label: 'No indicator' },
    { value: 'check', label: "Green checkmark (good approach, doesn't create a risk)" },
    { value: 'warning', label: 'Yellow warning (not ideal, but doesn’t create too much risk)' },
    { value: 'cancel', label: 'Red x (this is a big problem)' }
  ];

  const updateScopeData = (key: string, value: any) => {
    if (readonly) return; // Prevent changes in readonly mode
    setData(`methodologies.scope.${key}`, value);
  };

  const scopeData = data?.scope || defaultData.methodologies.scope;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scopeColumnToRemove, setScopeColumnToRemove] = useState<string | null>(null);

  const handleRemoveScopeColumn = (scopeColumnId: string) => {
    if (readonly) return;
    setScopeColumnToRemove(scopeColumnId);
    setIsModalOpen(true);
  };

  const handleConfirmRemoveScopeColumn = () => {
    if (readonly) return;
    if (scopeColumnToRemove) {
      const updatedScopeColumns = scopeData.columns.filter(
        (scopeColumn: any) => scopeColumn.id !== scopeColumnToRemove
      );
      setData('methodologies.scope.columns', updatedScopeColumns);
    }
    setIsModalOpen(false);
  };

  const handleCancelRemoveScopeColumn = () => {
    setIsModalOpen(false);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Title level={3}>Scope</Title>
      <Card>
        {(scopeData?.columns || []).map((scope, index) => (
          <Space direction="vertical" style={{ width: '100%', marginBottom: '20px' }}>
            <Space
              direction="horizontal"
              style={{ justifyContent: 'space-between', width: '100%' }}>
              <Typography.Title level={5}>Scope Column no. {index + 1}</Typography.Title>
              <Button
                type="primary"
                danger
                disabled={readonly}
                style={{ marginTop: '10px' }}
                onClick={() => handleRemoveScopeColumn(scope.id)}>
                Remove
              </Button>
            </Space>
            <Input
              placeholder="Name"
              disabled={readonly}
              value={scope.name}
              onChange={(e) => {
                if (readonly) return;
                const updatedScopeColumns = (scopeData?.columns || [])?.map((scopeColumn, i) => {
                  if (scopeColumn.id === scope.id) {
                    return {
                      ...scopeColumn,
                      name: e.target.value,
                      key: e.target.value.toLowerCase().trim().replace(/\s/g, '')
                    };
                  }
                  return scopeColumn;
                });
                setData('methodologies.scope.columns', updatedScopeColumns);
              }}
            />
            <Space align="center">
              <Title level={5} style={{ marginTop: '5px' }}>
                Column size:
              </Title>
              <Select
                placeholder="Select width size"
                disabled={readonly}
                value={scope.widthSize}
                style={{ width: '200px' }}
                onChange={(value) => {
                  if (readonly) return;
                  const updatedScopeColumns = (scopeData?.columns || [])?.map((scopeColumn, i) => {
                    if (scopeColumn.id === scope.id) {
                      return {
                        ...scopeColumn,
                        widthSize: value
                      };
                    }
                    return scopeColumn;
                  });
                  setData('methodologies.scope.columns', updatedScopeColumns);
                }}>
                <Select.Option value="xs">Extra Small</Select.Option>
                <Select.Option value="sm">Small</Select.Option>
                <Select.Option value="md">Medium</Select.Option>
                <Select.Option value="lg">Large</Select.Option>
                <Select.Option value="xl">Extra Large</Select.Option>
              </Select>
            </Space>
          </Space>
        ))}
        <Button
          type="primary"
          disabled={readonly}
          style={{ marginTop: '10px' }}
          onClick={() => {
            if (readonly) return;
            setData('methodologies.scope.columns', [
              ...(scopeData?.columns || []),
              { id: uuidv4(), name: '', key: '', widthSize: 'md' }
            ]);
          }}>
          Add Scope Column
        </Button>
      </Card>
      <Divider />
      <Title level={5}>Citation list (enter as a numbered list)</Title>
      <TipTap
        value={scopeData.citations}
        onChange={(value) => updateScopeData('citations', value)}
        readonly={readonly}
      />
      <Card style={{ marginBottom: '16px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text strong>Summary</Typography.Text>
          <TipTap
            value={scopeData.summary}
            onChange={(value) => updateScopeData('summary', value)}
            readonly={readonly}
          />
          {scopeData.showGraph && (
            <Space direction="vertical" style={{ width: '100%', marginTop: '16px' }}>
              <Typography.Text strong>Graph Title:</Typography.Text>
              <Input
                placeholder="Graph Title"
                value={scopeData.graphTitle}
                onChange={(e) => updateScopeData('graphTitle', e.target.value)}
                disabled={readonly}
              />
              <Typography.Text strong>
                Graph Title Tooltip Text (Optional, if tooltip needed at the end of title):
              </Typography.Text>
              <Input.TextArea
                placeholder="Tooltip text"
                value={scopeData.graphTitleTooltipText}
                onChange={(e) => updateScopeData('graphTitleTooltipText', e.target.value)}
                disabled={readonly}
              />
              <Typography.Text strong>Graph Takeaway:</Typography.Text>
              <Input.TextArea
                placeholder="Graph Takeaway"
                value={scopeData.graphDescription}
                onChange={(e) => updateScopeData('graphDescription', e.target.value)}
                disabled={readonly}
                showCount
                maxLength={180}
              />
            </Space>
          )}
          <Divider />
          <Space direction="vertical" style={{ width: '100%', marginTop: '16px' }}>
            <Typography.Text strong>Impact of Scope on GHG integrity:</Typography.Text>
            <Select
              style={{ width: '100%' }}
              mode="multiple"
              value={scopeData.ghgImpact}
              onChange={(value) => updateScopeData('ghgImpact', value)}
              options={impactLevels}
              disabled={readonly}
            />
            <Typography.Text strong>Rationale:</Typography.Text>
            <Input.TextArea
              placeholder="GHG Impact Takeaway"
              value={scopeData.ghgImpactDescription}
              onChange={(e) => updateScopeData('ghgImpactDescription', e.target.value)}
              disabled={readonly}
              showCount
              maxLength={400}
            />
          </Space>
          <Divider />
          <Space direction="vertical" style={{ width: '100%', marginTop: '16px' }}>
            <Typography.Text strong>Impact of methodology selection on Scope risk:</Typography.Text>
            <Select
              style={{ width: '100%' }}
              mode="multiple"
              value={scopeData.methodologyImpact}
              onChange={(value) => updateScopeData('methodologyImpact', value)}
              options={impactLevels}
              disabled={readonly}
            />
            <Typography.Text strong>Rationale:</Typography.Text>
            <Input.TextArea
              placeholder="Methodology Impact Takeaway"
              value={scopeData.methodologyImpactDescription}
              onChange={(e) => updateScopeData('methodologyImpactDescription', e.target.value)}
              disabled={readonly}
              showCount
              maxLength={400}
            />
          </Space>
        </Space>
      </Card>

      <Card>
        {scopeData.data.map((item: any, itemIndex: number) => (
          <Space key={item.id} direction="vertical" style={{ width: '100%', marginBottom: '24px' }}>
            <Space
              direction="horizontal"
              style={{ justifyContent: 'space-between', width: '100%' }}>
              <Typography.Title level={5}>Methodology #{itemIndex + 1}</Typography.Title>
              {scopeData.data.length > 1 && (
                <Button
                  type="primary"
                  danger
                  onClick={() => {
                    if (readonly) return;
                    const updatedData = scopeData.data.filter((d: any) => d.id !== item.id);
                    updateScopeData('data', updatedData);
                  }}
                  disabled={readonly}>
                  Remove
                </Button>
              )}
            </Space>

            <Space direction="vertical" style={{ width: '100%' }}>
              <Input
                placeholder="Standard"
                value={item.standard}
                onChange={(e) => {
                  if (readonly) return;
                  const updatedData = scopeData.data.map((d: any) => {
                    if (d.id === item.id) {
                      return { ...d, standard: e.target.value };
                    }
                    return d;
                  });
                  updateScopeData('data', updatedData);
                }}
                style={{ marginBottom: '12px' }}
                disabled={readonly}
              />

              <Input
                placeholder="Methodology"
                value={item.methodology}
                onChange={(e) => {
                  if (readonly) return;
                  const updatedData = scopeData.data.map((d: any) => {
                    if (d.id === item.id) {
                      return { ...d, methodology: e.target.value };
                    }
                    return d;
                  });
                  updateScopeData('data', updatedData);
                }}
                style={{ marginBottom: '12px' }}
                disabled={readonly}
              />

              <Input
                placeholder="Version"
                value={item.version}
                onChange={(e) => {
                  if (readonly) return;
                  const updatedData = scopeData.data.map((d: any) => {
                    if (d.id === item.id) {
                      return { ...d, version: e.target.value };
                    }
                    return d;
                  });
                  updateScopeData('data', updatedData);
                }}
                style={{ marginBottom: '16px' }}
                disabled={readonly}
              />
              <Card type="inner" title="" style={{ marginBlock: '16px' }}>
                {data.scope.columns.map((scopeColumn, index) => (
                  <Space direction="vertical" style={{ width: '100%', marginBottom: '20px' }}>
                    <Typography.Title level={5}>{scopeColumn.name}</Typography.Title>
                    <TipTap
                      value={item[scopeColumn.key]}
                      readonly={readonly}
                      onChange={(value) => {
                        if (readonly) return;
                        const updatedMethodologies = data.scope.data.map((m) => {
                          if (m.id === item.id) {
                            return {
                              ...m,
                              [scopeColumn.key]: value
                            };
                          }
                          return m;
                        });
                        setData('methodologies.scope.data', updatedMethodologies);
                      }}
                    />
                  </Space>
                ))}
              </Card>
              <Card type="inner" title="Risk" style={{ marginBottom: '16px' }}>
                {item.risk.impact.map((impact: any) => (
                  <Space
                    key={impact.id}
                    direction="horizontal"
                    style={{ marginBottom: '12px', width: '100%' }}>
                    <Select
                      style={{ width: '150px' }}
                      value={impact.impact}
                      onChange={(value) => {
                        if (readonly) return;
                        const updatedImpacts = item.risk.impact.map((i: any, idx: number) => {
                          if (i.id === impact.id) {
                            return { ...i, impact: value };
                          }
                          return i;
                        });
                        const updatedData = scopeData.data.map((d: any) => {
                          if (d.id === item.id) {
                            return {
                              ...d,
                              risk: {
                                ...d.risk,
                                impact: updatedImpacts
                              }
                            };
                          }
                          return d;
                        });
                        updateScopeData('data', updatedData);
                      }}
                      options={risks}
                      disabled={readonly}
                    />

                    <Input
                      placeholder="Qualifying text (optional) E.g. for flaring"
                      style={{ flex: 1 }}
                      value={impact.description}
                      onChange={(e) => {
                        if (readonly) return;
                        const updatedImpacts = item.risk.impact.map((i: any) => {
                          if (i.id === impact.id) {
                            return { ...i, description: e.target.value };
                          }
                          return i;
                        });
                        const updatedData = scopeData.data.map((d: any) => {
                          if (d.id === item.id) {
                            return {
                              ...d,
                              risk: {
                                ...d.risk,
                                impact: updatedImpacts
                              }
                            };
                          }
                          return d;
                        });
                        updateScopeData('data', updatedData);
                      }}
                      disabled={readonly}
                    />

                    {item.risk.impact.length > 1 && (
                      <Button
                        danger
                        size="small"
                        onClick={() => {
                          if (readonly) return;
                          const updatedImpacts = item.risk.impact.filter(
                            (i: any) => i.id !== impact.id
                          );
                          const updatedData = scopeData.data.map((d: any) => {
                            if (d.id === item.id) {
                              return {
                                ...d,
                                risk: {
                                  ...d.risk,
                                  impact: updatedImpacts
                                }
                              };
                            }
                            return d;
                          });
                          updateScopeData('data', updatedData);
                        }}
                        disabled={readonly}>
                        Remove
                      </Button>
                    )}
                  </Space>
                ))}

                <Button
                  type="dashed"
                  style={{ width: '100%', marginBottom: '12px' }}
                  onClick={() => {
                    if (readonly) return;
                    const newImpact = { id: uuidv4(), impact: 'low', description: '' };
                    const updatedData = scopeData.data.map((d: any) => {
                      if (d.id === item.id) {
                        return {
                          ...d,
                          risk: {
                            ...d.risk,
                            impact: [...d.risk.impact, newImpact]
                          }
                        };
                      }
                      return d;
                    });
                    updateScopeData('data', updatedData);
                  }}
                  disabled={readonly}>
                  + Add impact level (optional, for cases where risk varies)
                </Button>

                <Typography.Text strong>Risk Details</Typography.Text>
                <TipTap
                  value={item.risk.data}
                  onChange={(value) => {
                    if (readonly) return;
                    const updatedData = scopeData.data.map((d: any) => {
                      if (d.id === item.id) {
                        return {
                          ...d,
                          risk: {
                            ...d.risk,
                            data: value
                          }
                        };
                      }
                      return d;
                    });
                    updateScopeData('data', updatedData);
                  }}
                  readonly={readonly}
                />
              </Card>

              <Card type="inner" title="Transparency Risk" style={{ marginBottom: '16px' }}>
                {(Array.isArray(item?.transparencyRisk?.impact)
                  ? item?.transparencyRisk?.impact
                  : [item?.transparencyRisk?.impact]
                ).map((impact: any) => (
                  <Space
                    key={impact.id}
                    direction="horizontal"
                    style={{ marginBottom: '12px', width: '100%' }}>
                    <Select
                      style={{ width: '150px' }}
                      value={impact.impact}
                      onChange={(value) => {
                        if (readonly) return;
                        const updatedImpacts = (
                          Array.isArray(item?.transparencyRisk?.impact)
                            ? item?.transparencyRisk?.impact
                            : [item?.transparencyRisk?.impact]
                        ).map((i: any, idx: number) => {
                          if (i.id === impact.id) {
                            if (!Array.isArray(item?.transparencyRisk?.impact)) {
                              return { impact: value, id: impact.id || uuidv4(), description: '' };
                            } else {
                              return { ...i, impact: value };
                            }
                          }
                          return i;
                        });
                        const updatedData = scopeData.data.map((d: any) => {
                          if (d.id === item.id) {
                            return {
                              ...d,
                              transparencyRisk: {
                                ...d.transparencyRisk,
                                impact: updatedImpacts
                              }
                            };
                          }
                          return d;
                        });
                        updateScopeData('data', updatedData);
                      }}
                      options={risks}
                      disabled={readonly}
                    />

                    <Input
                      placeholder="Qualifying text (optional) E.g. for flaring"
                      style={{ flex: 1 }}
                      value={impact.description}
                      onChange={(e) => {
                        if (readonly) return;
                        const updatedImpacts = (
                          Array.isArray(item?.transparencyRisk?.impact)
                            ? item?.transparencyRisk?.impact
                            : [item?.transparencyRisk?.impact]
                        ).map((i: any) => {
                          if (i.id === impact.id) {
                            if (!Array.isArray(item?.transparencyRisk?.impact)) {
                              return {
                                impact: i.impact,
                                id: impact.id || uuidv4(),
                                description: e.target.value
                              };
                            } else {
                              return { ...i, description: e.target.value };
                            }
                          }
                          return i;
                        });
                        const updatedData = scopeData.data.map((d: any) => {
                          if (d.id === item.id) {
                            return {
                              ...d,
                              transparencyRisk: {
                                ...d.transparencyRisk,
                                impact: updatedImpacts
                              }
                            };
                          }
                          return d;
                        });
                        updateScopeData('data', updatedData);
                      }}
                      disabled={readonly}
                    />

                    {item.transparencyRisk.impact.length > 1 && (
                      <Button
                        danger
                        size="small"
                        onClick={() => {
                          if (readonly) return;
                          const updatedImpacts = item.transparencyRisk.impact.filter(
                            (i: any) => i.id !== impact.id
                          );
                          const updatedData = scopeData.data.map((d: any) => {
                            if (d.id === item.id) {
                              return {
                                ...d,
                                transparencyRisk: {
                                  ...d.transparencyRisk,
                                  impact: updatedImpacts
                                }
                              };
                            }
                            return d;
                          });
                          updateScopeData('data', updatedData);
                        }}
                        disabled={readonly}>
                        Remove
                      </Button>
                    )}
                  </Space>
                ))}

                <Button
                  type="dashed"
                  style={{ width: '100%', marginBottom: '12px' }}
                  onClick={() => {
                    if (readonly) return;
                    const newImpact = { id: uuidv4(), impact: 'low', description: '' };
                    const updatedData = scopeData.data.map((d: any) => {
                      if (d.id === item.id) {
                        return {
                          ...d,
                          transparencyRisk: {
                            ...d.transparencyRisk,
                            impact: [...d.transparencyRisk.impact, newImpact]
                          }
                        };
                      }
                      return d;
                    });
                    updateScopeData('data', updatedData);
                  }}
                  disabled={readonly}>
                  + Add impact level (optional, for cases where risk varies)
                </Button>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Typography.Text strong>Transparency Details</Typography.Text>
                  <TipTap
                    value={item.transparencyRisk.data}
                    onChange={(value) => {
                      if (readonly) return;
                      const updatedData = scopeData.data.map((d: any) => {
                        if (d.id === item.id) {
                          return {
                            ...d,
                            transparencyRisk: {
                              ...d.transparencyRisk,
                              data: value
                            }
                          };
                        }
                        return d;
                      });
                      updateScopeData('data', updatedData);
                    }}
                    readonly={readonly}
                  />
                </Space>
              </Card>

              <Card type="inner" title="Mitigations" style={{ marginBottom: '16px' }}>
                <TipTap
                  value={item.mitigations.data}
                  onChange={(value) => {
                    if (readonly) return;
                    const updatedData = scopeData.data.map((d: any) => {
                      if (d.id === item.id) {
                        return {
                          ...d,
                          mitigations: {
                            ...d.mitigations,
                            data: value
                          }
                        };
                      }
                      return d;
                    });
                    updateScopeData('data', updatedData);
                  }}
                  readonly={readonly}
                />
              </Card>
            </Space>
            <Divider />
          </Space>
        ))}
      </Card>
      <Modal
        title="Confirm removal of scope column"
        open={isModalOpen}
        onOk={handleConfirmRemoveScopeColumn}
        onCancel={handleCancelRemoveScopeColumn}>
        All the tabs will be affected, are you sure you want to remove this scope column?
      </Modal>
    </Space>
  );
};

export default Scope;
